import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, RefreshCw, Save, Wallet, CheckCircle2, AlertTriangle, Radio } from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import { toast } from "sonner";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

export default function AdminMasterFeed() {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;
  const headers = useMemo(() => ({ "X-Admin-Token": token || "" }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", account_id: "" });

  useEffect(() => {
    if (!token) navigate("/admin/login");
  }, [token, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/master-feed`, { headers });
      setData(r.data);
      if (r.data?.feed) {
        setForm((f) => ({ ...f, email: r.data.feed.email || "", account_id: r.data.feed.account_id || "" }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/master-feed/sync-now`, {}, { headers });
      if (r.data?.ok) {
        toast.success(`Sync ok · Gain ${r.data.gain?.toFixed(2)}% · Monat ${r.data.monthly?.toFixed(2)}%`);
        await load();
      } else {
        toast.error(r.data?.error || "Sync fehlgeschlagen");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!form.email || !form.password || !form.account_id) {
      toast.error("Alle drei Felder Pflicht");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/master-feed`, form, { headers });
      if (r.data?.sync?.ok) {
        toast.success("Credentials gespeichert & Sync erfolgreich");
        setForm({ ...form, password: "" });
        await load();
      } else {
        toast.warning("Gespeichert – aber Sync fehlgeschlagen: " + (r.data?.sync?.error || "?"));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const feed = data?.feed;
  const stats = feed?.myfxbook_stats || {};
  const liveRows = data?.live_monthly_rows || [];

  return (
    <div className="min-h-screen bg-[var(--al-bg)]">
      <AdminTopNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tight" data-testid="master-feed-title">
              Master-MyFxBook-Feed
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Live-Rendite ab {data?.live_from || "Juli 2026"} für Track-Record & Landing-Page
            </p>
          </div>
          <button
            onClick={syncNow}
            disabled={busy || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-display font-semibold disabled:opacity-50"
            data-testid="master-feed-sync-now"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Jetzt synchronisieren
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
        ) : (
          <>
            {/* Status */}
            <section className="al-card mb-6 p-6" data-testid="master-feed-status">
              <div className="flex items-center gap-2 mb-4">
                <Radio className={`h-5 w-5 ${feed?.last_sync_at ? "text-emerald-500" : "text-slate-400"}`} />
                <h2 className="font-display font-bold text-lg">Live-Status</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatMini label="Gesamt (all-time)" value={stats.gain != null ? `${stats.gain.toFixed(2)}%` : "—"} tone="emerald" />
                <StatMini label="Dieser Monat" value={stats.monthly != null ? `${stats.monthly.toFixed(2)}%` : "—"} tone="violet" />
                <StatMini label="Heute" value={stats.daily != null ? `${stats.daily.toFixed(2)}%` : "—"} tone="sky" />
                <StatMini label="Balance" value={stats.balance != null ? `$${stats.balance.toLocaleString("de-CH", {maximumFractionDigits: 0})}` : "—"} />
              </div>
              <div className="mt-4 text-xs text-slate-500 flex flex-wrap gap-x-6 gap-y-1">
                <span>Account: <strong>{feed?.account_id || "—"}</strong></span>
                <span>MyFxBook-Login: <strong>{feed?.email || "—"}</strong></span>
                <span>Letzter Sync: <strong>{feed?.last_sync_at ? new Date(feed.last_sync_at).toLocaleString("de-CH") : "noch nie"}</strong></span>
                {feed?.last_error && (
                  <span className="inline-flex items-center gap-1 text-rose-600">
                    <AlertTriangle className="h-3 w-3" /> {feed.last_error}
                  </span>
                )}
              </div>
            </section>

            {/* Live Rows Preview */}
            <section className="al-card mb-6" data-testid="master-feed-preview">
              <div className="p-6 border-b border-[var(--al-line)]">
                <h2 className="font-display font-bold text-lg">Live-Monats-Preview</h2>
                <p className="text-sm text-slate-500">Werte, die aktuell auf Track-Record und Landing-Page erscheinen</p>
              </div>
              {liveRows.length === 0 ? (
                <div className="p-6 text-sm text-slate-500 italic">Noch keine Live-Monate berechnet — nach dem ersten Sync und wenn MyFxBook-Daten vorliegen.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]" data-testid="master-feed-preview-table">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="text-left px-6 py-3">Monat</th>
                        <th className="text-right px-6 py-3">Monatliche Rendite</th>
                        <th className="text-right px-6 py-3">Quelle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveRows.map((r, i) => (
                        <tr key={r.month || i} className="border-t border-[var(--al-line)]">
                          <td className="px-6 py-3 font-semibold">{r.month}</td>
                          <td className={`px-6 py-3 text-right font-mono-tab font-semibold ${r.monthly >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                            {r.monthly >= 0 ? "+" : ""}{r.monthly.toFixed(2)}%
                          </td>
                          <td className="px-6 py-3 text-right">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Credentials Form */}
            <section className="al-card p-6" data-testid="master-feed-form">
              <h2 className="font-display font-bold text-lg mb-4">Credentials aktualisieren</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">MyFxBook Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="master-feed-input-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">MyFxBook Passwort</label>
                  <input
                    type="password"
                    value={form.password}
                    placeholder="•••• (nur wenn ändern)"
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="master-feed-input-password"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Account-ID</label>
                  <input
                    type="text"
                    value={form.account_id}
                    onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="master-feed-input-accountid"
                  />
                </div>
              </div>
              <button
                onClick={save}
                disabled={busy || !form.email || !form.password || !form.account_id}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--al-purple)] hover:opacity-90 text-white text-sm font-display font-semibold disabled:opacity-50"
                data-testid="master-feed-save"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern & Sync
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

const StatMini = ({ label, value, tone = "slate" }) => {
  const toneMap = {
    emerald: "text-emerald-700",
    violet: "text-[var(--al-purple)]",
    sky: "text-sky-700",
    slate: "text-slate-900",
  };
  return (
    <div className="p-3 rounded-lg bg-slate-50">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{label}</div>
      <div className={`text-xl font-mono-tab font-black ${toneMap[tone]}`}>{value}</div>
    </div>
  );
};
